import type Store from "@ember-data/store";
import type HealthCentre from "parog-web/models/health-centre";
import type HealthCentreTag from "parog-web/models/health-centre-tag";
import type Medic from "parog-web/models/medic";

interface PatientListFilterArgs {
  serializedFilter: string;
  store: Store;
  tabType?: string;
}

export interface PatientApiFilters {
  enrolled?: string;
  not_enrolled?: string;
  disenrolled?: string;
  medics?: string[];
  health_centres?: string[];
  health_centre_tag?: string;
  of_health_centres?: string[];
  of_health_centre_configurator?: string[];
  furs?: string[];
  tls_consent_status?: string;
}

export interface JournalEventApiFilters {
  of_enrolled_patients?: string;
  of_not_enrolled_patients?: string;
  of_disenrolled_patients?: string;
  of_patients_followed_by_medics?: string[];
  of_patients_followed_by_health_centres?: string[];
  of_patients_followed_by_health_centre_configurator?: string[];
  of_patients_followed_by_health_centre_tag?: string;
  of_medics?: string;
  of_medic_belonging_to_health_centres?: string[];
  task_type?: string;
  responsible_id?: string;
  fur?: string;
}

type PatientStatusFilter =
  | "enrolled"
  | "not-enrolled"
  | "disenrolled"
  | undefined;

export type TlsConsentStatus = "all" | "tls" | "data-only";

export const filterSeparator = "_";
export const keyValueSeparator = "%3D";

interface Filters {
  fur: string;
  medicId?: string;
  // healthCentreConfigurator will filter only with configurator health centre cohort,
  // healthCentre will filter with both configurator and support health centre cohort
  healthCentreConfiguratorId?: string;
  healthCentreId?: string;
  healthCentreTagId?: string;
  patientStatus: string;
  taskType?: string;
  responsibleId?: string;
  tlsConsentStatus?: TlsConsentStatus;
}

export class PatientListFilterParser {
  filters: Filters;
  store: Store;

  get healthCentre(): HealthCentre | null {
    if (
      this.filters.healthCentreId &&
      this.filters.healthCentreId != "undefined"
    ) {
      return this.store.peekRecord(
        "health-centre",
        this.filters.healthCentreId
      ) as HealthCentre;
    }

    return null;
  }

  set healthCentre(filter: string | undefined) {
    this.filters.healthCentreId = filter;
  }

  get healthCentreConfigurator(): HealthCentre | null {
    if (this.filters.healthCentreConfiguratorId) {
      return this.store.peekRecord(
        "health-centre",
        this.filters.healthCentreConfiguratorId
      ) as HealthCentre;
    }

    return null;
  }

  set healthCentreConfigurator(healthCentreId: string | undefined) {
    this.filters.healthCentreConfiguratorId = healthCentreId;
  }

  get healthCentreTag(): HealthCentreTag | null {
    if (
      this.filters.healthCentreTagId &&
      this.filters.healthCentreTagId != "undefined"
    ) {
      return this.store.peekRecord(
        "health-centre-tag",
        this.filters.healthCentreTagId
      ) as HealthCentreTag;
    }

    return null;
  }

  set healthCentreTag(filter: string | undefined) {
    this.filters.healthCentreTagId = filter;
  }

  get isAllHealthCentresSelected(): boolean {
    return (
      this.filters.healthCentreId == "all" ||
      this.filters.healthCentreConfiguratorId == "all"
    );
  }

  get isAllMedicsSelected(): boolean {
    return this.filters.medicId == "all";
  }

  get isValid(): boolean {
    return Boolean(
      this.patientStatus &&
        (this.medic || this.isAllMedicsSelected) &&
        (this.healthCentre ||
          this.healthCentreConfigurator ||
          this.isAllHealthCentresSelected ||
          this.healthCentreTag)
    );
  }

  get medic(): Medic | null {
    if (this.filters.medicId && this.filters.medicId != "undefined") {
      return this.store.peekRecord("medic", this.filters.medicId);
    }

    return null;
  }

  set medic(filter: string | undefined) {
    this.filters.medicId = filter;
  }

  get patientFur(): string {
    return this.filters.fur;
  }

  set patientFur(filter: string | undefined) {
    this.filters.fur = filter || "";
  }

  get patientStatus(): PatientStatusFilter {
    const status = this.filters.patientStatus;
    return ["enrolled", "not-enrolled", "disenrolled"].includes(status)
      ? (status as PatientStatusFilter)
      : undefined;
  }

  set patientStatus(filter: string | undefined) {
    this.filters.patientStatus = filter || "";
  }

  get responsibleId(): string | undefined {
    return this.filters.responsibleId;
  }

  set responsibleId(filter: string | undefined) {
    this.filters.responsibleId = filter;
  }

  get serializedFilter(): string {
    return this.serialize(this.filters);
  }

  get taskType(): string | undefined {
    return this.filters.taskType;
  }

  set taskType(filter: string | undefined) {
    this.filters.taskType = filter;
  }

  get tlsConsentStatus(): TlsConsentStatus | undefined {
    return this.filters.tlsConsentStatus;
  }

  set tlsConsentStatus(filter: TlsConsentStatus) {
    this.filters.tlsConsentStatus = filter;
  }

  constructor({ serializedFilter, store }: PatientListFilterArgs) {
    this.filters = this.deserialize(serializedFilter);
    this.store = store;
  }

  private deserialize(serializedFilter: string): Filters {
    return serializedFilter?.split(filterSeparator).reduce((acc, cur) => {
      const [key, value] = cur.split(keyValueSeparator);
      acc[key] = value;
      return acc;
    }, {} as Filters);
  }

  private serialize(filters: Filters): string {
    return Object.keys(filters)
      .map(key => `${key}${keyValueSeparator}${filters[key]}`)
      .join(filterSeparator);
  }
}

/**
 * Helps transform a filter string to options suitable for calling the API.
 */
export default class PatientListFilterSerializer {
  parser: PatientListFilterParser;
  serializedFilter: string;
  store: Store;

  get journalEventsApiFilter(): JournalEventApiFilters {
    const filters: JournalEventApiFilters = {};
    if (this.parser.medic) {
      filters.of_patients_followed_by_medics = [this.parser.medic.id];
    }
    if (this.parser.healthCentre) {
      filters.of_patients_followed_by_health_centres = [
        this.parser.healthCentre.id
      ];
    }

    if (this.parser.healthCentreConfigurator) {
      filters.of_patients_followed_by_health_centre_configurator = [
        this.parser.healthCentreConfigurator.id
      ];
    }

    if (this.parser.healthCentreTag) {
      filters.of_patients_followed_by_health_centre_tag =
        this.parser.healthCentreTag.id;
    }

    if (this.parser.patientFur && this.parser.patientFur != "all") {
      filters["fur"] = this.parser.patientFur;
    }

    if (this.parser.patientStatus) {
      filters[`of_${this.parser.patientStatus.replace("-", "_")}_patients`] =
        "";
    }

    if (this.parser.tlsConsentStatus && this.parser.tlsConsentStatus != "all") {
      filters["tls_consent_status"] = this.parser.tlsConsentStatus;
    }

    return filters;
  }

  get medicRelatedJournalEventApiFilter(): JournalEventApiFilters {
    const filters: JournalEventApiFilters = {};

    if (this.parser.medic) {
      filters.of_medics = this.parser.medic.id;
    } else if (this.parser.healthCentre) {
      filters.of_medic_belonging_to_health_centres = [
        this.parser.healthCentre.id
      ];
    }

    return filters;
  }

  get patientApiFilter(): PatientApiFilters {
    const filters: PatientApiFilters = {};

    if (this.parser.medic) {
      filters.medics = [this.parser.medic.id];
    }
    if (this.parser.healthCentre) {
      filters.of_health_centres = [this.parser.healthCentre.id];
    }

    if (this.parser.healthCentreConfigurator) {
      filters.of_health_centre_configurator = [
        this.parser.healthCentreConfigurator.id
      ];
    }

    if (this.parser.healthCentreTag) {
      filters.health_centre_tag = this.parser.healthCentreTag.id;
    }

    if (this.parser.patientFur && this.parser.patientFur != "all") {
      filters.furs = [this.parser.patientFur];
    }

    if (this.parser.patientStatus) {
      filters[this.parser.patientStatus.replace("-", "_")] = "";
    }

    if (this.parser.tlsConsentStatus && this.parser.tlsConsentStatus != "all") {
      filters.tls_consent_status = this.parser.tlsConsentStatus;
    }

    return filters;
  }

  get translationKey(): { status?: string; name?: string } {
    let name;
    if (this.parser.medic) {
      name = this.parser.medic.fullName;
    }

    if (this.parser.healthCentre) {
      name = this.parser.healthCentre.name;
    }

    if (this.parser.healthCentreTag) {
      name = this.parser.healthCentreTag.tagTitle;
    }

    return {
      status: this.parser.patientStatus,
      name
    };
  }

  constructor({ serializedFilter, store }: PatientListFilterArgs) {
    this.serializedFilter = serializedFilter;
    this.store = store;

    this.parser = new PatientListFilterParser({
      serializedFilter,
      store
    });
  }
}
